import React from 'react';
import styled from 'styled-components';
import { TokenUsage } from '../types/asset';

const Container = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 20px;
  margin: 15px 0;
  color: white;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
`;

const CompletionBadge = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(255, 255, 255, 0.2);
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  backdrop-filter: blur(10px);
`;

const Title = styled.h3`
  margin: 0 0 15px 0;
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Icon = styled.span`
  font-size: 20px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 15px;
  margin-bottom: 15px;
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 12px;
  text-align: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 12px;
  opacity: 0.9;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ProgressSection = styled.div`
  margin-top: 15px;
`;

const ProgressBar = styled.div<{ $percentage: number }>`
  height: 8px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  overflow: hidden;
  margin: 8px 0;

  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${props => Math.min(props.$percentage, 100)}%;
    background: linear-gradient(90deg, #4ade80, #22c55e);
    border-radius: 4px;
    transition: width 0.3s ease;
  }
`;

const ProgressLabel = styled.div`
  font-size: 12px;
  opacity: 0.9;
  display: flex;
  justify-content: space-between;
`;

const DurationInfo = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 12px;
  margin-top: 15px;
  font-size: 14px;
  text-align: center;
`;

const CostEstimate = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 12px;
  margin-top: 10px;
  font-size: 14px;
  text-align: center;
`;

interface TokenUsageDisplayProps {
  tokenUsage: TokenUsage;
  title?: string;
  showCost?: boolean;
}

const TokenUsageDisplay: React.FC<TokenUsageDisplayProps> = ({ 
  tokenUsage, 
  title = "Token Usage", 
  showCost = false 
}) => {
  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat().format(num);
  };

  const formatDuration = (seconds: number): string => {
    // Format AI processing duration in a user-friendly way
    if (seconds < 60) {
      return `${seconds.toFixed(1)}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}m ${remainingSeconds.toFixed(0)}s`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    }
  };

  const calculateCost = (totalTokens: number): number => {
    // Rough estimate: GPT-4 pricing (as of 2024)
    // Input: $0.03 per 1K tokens, Output: $0.06 per 1K tokens
    const inputCost = (tokenUsage.total_prompt_tokens / 1000) * 0.03;
    const outputCost = (tokenUsage.total_completion_tokens / 1000) * 0.06;
    return inputCost + outputCost;
  };

  const calculateEfficiency = (): number => {
    // Efficiency score = (completion tokens / total tokens) × 100
    // Higher score = more AI-generated content relative to input instructions
    // Lower score = more input instructions relative to AI output
    if (tokenUsage.total_tokens === 0) return 0;
    return (tokenUsage.total_completion_tokens / tokenUsage.total_tokens) * 100;
  };

  return (
    <Container>
      <CompletionBadge>
        {tokenUsage.processing_duration_seconds ? 'Processing' : 'Completed'}
      </CompletionBadge>
      <Title>
        <Icon>🧠</Icon>
        {title}
      </Title>
      
      <StatsGrid>
        <StatCard>
          <StatValue>{formatNumber(tokenUsage.total_tokens)}</StatValue>
          <StatLabel>Total Tokens</StatLabel>
        </StatCard>
        
        <StatCard>
          <StatValue>{formatNumber(tokenUsage.total_prompt_tokens)}</StatValue>
          <StatLabel>Prompt Tokens</StatLabel>
        </StatCard>
        
        <StatCard>
          <StatValue>{formatNumber(tokenUsage.total_completion_tokens)}</StatValue>
          <StatLabel>Completion Tokens</StatLabel>
        </StatCard>
        
        <StatCard>
          <StatValue>{tokenUsage.api_calls_count}</StatValue>
          <StatLabel>API Calls</StatLabel>
        </StatCard>
      </StatsGrid>

      <ProgressSection>
        <ProgressLabel>
          <span>Efficiency Score</span>
          <span>{calculateEfficiency().toFixed(1)}%</span>
        </ProgressLabel>
        <ProgressBar $percentage={calculateEfficiency()} />
      </ProgressSection>

      {tokenUsage.processing_duration_seconds && (
        <DurationInfo>
          ⏱️ AI Processing Time: {formatDuration(tokenUsage.processing_duration_seconds)}
        </DurationInfo>
      )}

      {showCost && (
        <CostEstimate>
          💰 Estimated Cost: ${calculateCost(tokenUsage.total_tokens).toFixed(4)}
        </CostEstimate>
      )}
    </Container>
  );
};

export default TokenUsageDisplay; 